import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Link as ChakraLink,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { fetchManualeGiocoBySlug } from '../utils/manualeGiochiApi';
import { formatNumeroSquadreLabel } from '../utils/manualeGiocoSquadre';
import { renderBlocksRichText } from '../components/logbook/richTextBlocks';
import { LivelloStars } from '../components/manuale/LivelloStars';
import { COMPETITIVITA, TIPOLOGIE } from '../constants/manualeGiocoFilters';
import FormaCerchioDisplay from '../components/manuale-giochi/FormaCerchioDisplay';
import ManualeGiocoDetailSection from '../components/manuale-giochi/ManualeGiocoDetailSection';
import SchemaGrazziniChart from '../components/manuale-giochi/SchemaGrazziniChart';
import {
  resolveSchemaGrazziniForDisplay,
  SCHEMA_GRAZZINI_AXES,
} from '../constants/schemaGrazzini';

const formatAutoreLabel = (socio) => {
  const name = `${socio.nomeSocio || ''} ${socio.cognomeSocio || ''}`.trim();
  if (!name) return '';
  if (socio.numeroGiochi != null) {
    return `${name} (${socio.numeroGiochi})`;
  }
  return name;
};

/** Strapi v5: media multipli a volte come array, a volte `{ data: [...] }`. */
const asMediaArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  const inner = val?.data;
  if (!Array.isArray(inner)) return [];
  return inner.map((item) => {
    if (item?.attributes && typeof item.attributes === 'object') {
      return {
        id: item.id,
        documentId: item.documentId,
        ...item.attributes,
      };
    }
    return item;
  });
};

const galleryImageUrl = (m) => {
  if (!m || typeof m !== 'object') return null;
  const f = m.formats || {};
  return f.medium?.url || f.small?.url || f.large?.url || m.url || null;
};

const documentLinkUrl = (d) => {
  if (!d || typeof d !== 'object') return null;
  return d.url || d.formats?.thumbnail?.url || null;
};

const tipologiaLabel = (v) =>
  TIPOLOGIE.find((t) => t.value === v)?.label || v || '';

const competitivitaLabel = (v) =>
  COMPETITIVITA.find((t) => t.value === v)?.label || v || '';

const BlockSection = ({ title, blocks }) => {
  if (!Array.isArray(blocks) || !blocks.length) return null;
  return (
    <Box as="section" mb={8}>
      <Heading size="md" mb={3}>
        {title}
      </Heading>
      <Box fontSize="md" lineHeight="tall" color="chakra-body-text">
        {renderBlocksRichText(blocks, { prose: true })}
      </Box>
    </Box>
  );
};

const chipsFromMulti = (val) => {
  if (val == null) return [];
  if (Array.isArray(val)) return val.filter(Boolean).map(String);
  if (typeof val === 'string') {
    const t = val.trim();
    if (!t) return [];
    try {
      const j = JSON.parse(t);
      if (Array.isArray(j)) return j.map(String).filter(Boolean);
    } catch {
      /* ignore */
    }
    return t
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

const ChipSection = ({ title, items }) => {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <Box as="section" mb={6}>
      <Heading size="md" mb={3}>
        {title}
      </Heading>
      <Wrap>
        {items.map((item) => (
          <WrapItem key={item}>
            <Badge variant="subtle">{item}</Badge>
          </WrapItem>
        ))}
      </Wrap>
    </Box>
  );
};

function ManualeGiocoDetail() {
  const { slug } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const panelBg = useColorModeValue('white', 'gray.800');
  const panelBorder = useColorModeValue('gray.100', 'gray.700');
  const setupPreBg = useColorModeValue('gray.50', 'gray.900');
  const setupPreBorder = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const json = await fetchManualeGiocoBySlug(slug);
        if (!cancelled) setEntry(json.data || null);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Errore');
          setEntry(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <Box bg={pageBg} minH="100vh" py={{ base: 6, md: 10 }}>
        <Container maxW="3xl">
          <Box
            bg={panelBg}
            borderRadius="xl"
            borderWidth="1px"
            borderColor={panelBorder}
            boxShadow="sm"
            px={{ base: 5, md: 8 }}
            py={12}
          >
            <Text>Caricamento…</Text>
          </Box>
        </Container>
      </Box>
    );
  }

  if (error || !entry) {
    return (
      <Box bg={pageBg} minH="100vh" py={{ base: 6, md: 10 }}>
        <Container maxW="3xl">
          <Box
            bg={panelBg}
            borderRadius="xl"
            borderWidth="1px"
            borderColor={panelBorder}
            boxShadow="sm"
            px={{ base: 5, md: 8 }}
            py={10}
          >
            <Text color="red.500">{error || 'Gioco non trovato.'}</Text>
            <Button as={Link} to="/manuale-giochi" mt={4} variant="outline">
              Torna al manuale
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const titolo = entry.titolo || 'Scheda gioco';
  const schemaGrazziniMerged = resolveSchemaGrazziniForDisplay(
    entry.schemaGrazzini
  );
  const galleryShots = asMediaArray(entry.immagini)
    .map((m) => {
      const src = galleryImageUrl(m);
      return {
        m,
        src,
        key: m.id ?? m.documentId ?? src,
      };
    })
    .filter((x) => x.src);

  const documentiList = asMediaArray(entry.documenti).filter((d) =>
    documentLinkUrl(d)
  );

  let setupCampoDisplay = null;
  const sc = entry.setupCampo;
  if (sc != null) {
    if (typeof sc === 'string') {
      const t = sc.trim();
      if (t) setupCampoDisplay = t;
    } else if (Array.isArray(sc) && sc.length) {
      setupCampoDisplay = JSON.stringify(sc, null, 2);
    } else if (typeof sc === 'object' && Object.keys(sc).length) {
      setupCampoDisplay = JSON.stringify(sc, null, 2);
    }
  }

  const schedaTecnicaCells = [];
  if (entry.numeroGiocatoriMin != null || entry.numeroGiocatoriMax != null) {
    schedaTecnicaCells.push({
      key: 'giocatori',
      label: 'Numero di giocatrici e giocatori',
      value: `${entry.numeroGiocatoriMin ?? '—'} – ${entry.numeroGiocatoriMax ?? '—'}`,
    });
  }
  if (entry.etaMin != null || entry.etaMax != null) {
    const min = entry.etaMin;
    const max = entry.etaMax;
    let etaValue = '';
    if (min != null && max != null) {
      etaValue = `${min} – ${max}`;
    } else if (min != null && max == null) {
      etaValue = `Da ${min} anni in su`;
    } else {
      etaValue = `Fino a ${max} anni`;
    }
    schedaTecnicaCells.push({
      key: 'eta',
      label: 'Età consigliata (anni)',
      value: etaValue,
    });
  }
  if (entry.durataMinuti != null) {
    schedaTecnicaCells.push({
      key: 'durata',
      label: 'Durata indicativa',
      value: `${entry.durataMinuti} min`,
    });
  }
  if (entry.tempoPreparazioneMinuti != null) {
    schedaTecnicaCells.push({
      key: 'preparazione',
      label: 'Tempo di preparazione',
      value: `${entry.tempoPreparazioneMinuti} min`,
    });
  }
  const squadreLabel = formatNumeroSquadreLabel(
    entry.numeroSquadre,
    entry.numeroSquadreMax
  );
  if (squadreLabel) {
    schedaTecnicaCells.push({
      key: 'squadre',
      label: 'Squadre',
      value: squadreLabel,
    });
  }

  const livelliCells = [];
  if (entry.tipologia) {
    livelliCells.push({
      key: 'tipologia',
      label: 'Tipologia',
      value: tipologiaLabel(entry.tipologia),
    });
  }
  if (entry.competitivita) {
    livelliCells.push({
      key: 'competitivita',
      label: 'Competitività',
      value: competitivitaLabel(entry.competitivita),
    });
  }
  if (entry.energia) {
    livelliCells.push({
      key: 'energia',
      label: 'Energia',
      value: <LivelloStars value={entry.energia} boxSize="0.95rem" />,
    });
  }
  if (entry.complessita) {
    livelliCells.push({
      key: 'complessita',
      label: 'Complessità',
      value: <LivelloStars value={entry.complessita} boxSize="0.95rem" />,
    });
  }
  if (entry.rischio) {
    livelliCells.push({
      key: 'rischio',
      label: 'Rischio',
      value: <LivelloStars value={entry.rischio} boxSize="0.95rem" />,
    });
  }

  const renderInfoGrid = (cells) => {
    if (!cells.length) return null;
    return (
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={6} spacingY={4}>
        {cells.map(({ key, label, value }) => (
          <Box key={key}>
            <Text
              fontSize="xs"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.06em"
              mb={1}
            >
              {label}
            </Text>
            {typeof value === 'string' || typeof value === 'number' ? (
              <Text fontWeight="medium">{value}</Text>
            ) : (
              <Box>{value}</Box>
            )}
          </Box>
        ))}
      </SimpleGrid>
    );
  };

  return (
    <Box bg={pageBg} minH="100vh" py={{ base: 6, md: 10 }}>
      <Container maxW={schemaGrazziniMerged ? '6xl' : '3xl'}>
        <Helmet>
          <title>{titolo} | Manuale giochi CIL</title>
        </Helmet>
        <Box
          bg={panelBg}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={panelBorder}
          boxShadow="sm"
          px={{ base: 5, md: 8 }}
          py={{ base: 6, md: 8 }}
          overflow="visible"
        >
          <Button
            as={Link}
            to="/manuale-giochi"
            variant="link"
            colorScheme="teal"
            mb={4}
          >
            ← Manuale giochi
          </Button>

          <Heading size="xl" mb={2}>
            {titolo}
          </Heading>

          {entry.autori?.length > 0 ? (
            <ManualeGiocoDetailSection title="Autori">
              <Text fontSize="sm" color="gray.600">
                {entry.autori
                  .map(formatAutoreLabel)
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
            </ManualeGiocoDetailSection>
          ) : null}

          {entry.descrizioneBreve ? (
            <ManualeGiocoDetailSection title="Descrizione breve">
              <Text fontSize="lg" color="gray.600">
                {entry.descrizioneBreve}
              </Text>
            </ManualeGiocoDetailSection>
          ) : null}

          {schedaTecnicaCells.length > 0 ? (
            <ManualeGiocoDetailSection title="Scheda tecnica">
              {renderInfoGrid(schedaTecnicaCells)}
            </ManualeGiocoDetailSection>
          ) : null}

          {livelliCells.length > 0 ? (
            <ManualeGiocoDetailSection title="Livelli e intensità">
              {renderInfoGrid(livelliCells)}
            </ManualeGiocoDetailSection>
          ) : null}

          <FormaCerchioDisplay value={entry.formaCerchio} />

          <ChipSection
            title="Materiali"
            items={chipsFromMulti(entry.materiali)}
          />

          {entry.campoTestuale && String(entry.campoTestuale).trim() ? (
            <ManualeGiocoDetailSection title="Campo e spazio">
              <Text whiteSpace="pre-wrap">
                {String(entry.campoTestuale).trim()}
              </Text>
            </ManualeGiocoDetailSection>
          ) : null}

          {setupCampoDisplay ? (
            <ManualeGiocoDetailSection title="Setup campo">
              <Box
                as="pre"
                fontSize="sm"
                fontFamily="mono"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                p={4}
                borderRadius="md"
                bg={setupPreBg}
                borderWidth="1px"
                borderColor={setupPreBorder}
              >
                {setupCampoDisplay}
              </Box>
            </ManualeGiocoDetailSection>
          ) : null}

          <BlockSection title="Preparazione" blocks={entry.preparazione} />
          <BlockSection title="Regole" blocks={entry.regole} />
          <BlockSection title="Varianti" blocks={entry.varianti} />
          <BlockSection title="Avvertenze" blocks={entry.avvertenze} />

          <ChipSection
            title="Categorie"
            items={chipsFromMulti(entry.categorieGioco)}
          />
          <ChipSection
            title="Meccaniche"
            items={chipsFromMulti(entry.meccanicheGioco)}
          />
          <ChipSection
            title="Fasce d'età"
            items={chipsFromMulti(entry.fasceEta)}
          />
          <ChipSection title="Spazi" items={chipsFromMulti(entry.spaziGioco)} />
          <ChipSection
            title="Obiettivi educativi"
            items={chipsFromMulti(entry.obiettiviEducativi)}
          />

          {schemaGrazziniMerged ? (
            <Box as="section" mb={10} overflow="visible">
              <Heading size="md" mb={4}>
                Schema di Grazzini-Hoffmann e Staccioli
              </Heading>
              {String(schemaGrazziniMerged.notes || '').trim() ? (
                <Text fontSize="sm" whiteSpace="pre-wrap" mb={4}>
                  {String(schemaGrazziniMerged.notes).trim()}
                </Text>
              ) : null}
              <Stack spacing={8} align="stretch" overflow="visible">
                <Flex justify="center" w="100%" minW={0} overflow="visible">
                  <Box w="100%" maxW="840px" overflow="visible">
                    <SchemaGrazziniChart
                      values={schemaGrazziniMerged.values}
                      sidePanel={false}
                      dimensionList={false}
                    />
                  </Box>
                </Flex>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Valori da 0 (non rilevante) a 5 (molto presente). Stessa
                    scala del diagramma.
                  </Text>
                  <SimpleGrid
                    columns={{ base: 1, md: 2 }}
                    spacing={{ base: 6, md: 8 }}
                    alignItems="start"
                  >
                    {[0, 1].map((col) => {
                      const mid = Math.ceil(SCHEMA_GRAZZINI_AXES.length / 2);
                      const axesCol =
                        col === 0
                          ? SCHEMA_GRAZZINI_AXES.slice(0, mid)
                          : SCHEMA_GRAZZINI_AXES.slice(mid);
                      return (
                        <TableContainer
                          key={col === 0 ? 'val-col-a' : 'val-col-b'}
                          w="100%"
                          minW={0}
                        >
                          <Table
                            size="sm"
                            variant="simple"
                            sx={{ th: { color: 'gray.600' } }}
                          >
                            <Thead>
                              <Tr>
                                <Th scope="col">Dimensione</Th>
                                <Th scope="col" isNumeric>
                                  Valore
                                </Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {axesCol.map((axis) => {
                                const val =
                                  schemaGrazziniMerged.values[axis.key] ?? 0;
                                return (
                                  <Tr key={axis.key}>
                                    <Td whiteSpace="pre-line">{axis.label}</Td>
                                    <Td isNumeric>{val}</Td>
                                  </Tr>
                                );
                              })}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      );
                    })}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Box>
          ) : null}

          {documentiList.length > 0 ? (
            <ManualeGiocoDetailSection title="Documenti">
              <Stack spacing={2}>
                {documentiList.map((doc) => {
                  const href = documentLinkUrl(doc);
                  return (
                    <ChakraLink
                      key={doc.id || doc.documentId || href}
                      href={href}
                      isExternal
                      color="teal.600"
                      fontWeight="medium"
                    >
                      {doc.name || doc.filename || doc.url || 'Allegato'}
                    </ChakraLink>
                  );
                })}
              </Stack>
            </ManualeGiocoDetailSection>
          ) : null}

          {galleryShots.length > 0 ? (
            <Box as="section" mt={2}>
              <Heading size="md" mb={3}>
                Galleria
              </Heading>
              <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={3}>
                {galleryShots.map(({ m, src, key }) => (
                  <Image
                    key={key}
                    src={src}
                    alt={m.alternativeText || m.name || titolo}
                    borderRadius="md"
                    w="100%"
                    h={{ base: '140px', sm: '160px' }}
                    objectFit="cover"
                    loading="lazy"
                  />
                ))}
              </SimpleGrid>
            </Box>
          ) : null}
        </Box>
      </Container>
    </Box>
  );
}

export default ManualeGiocoDetail;
